import { CommitStrategy, RealtimeEvents, Scribe, type RealtimeConnection } from '@elevenlabs/client'
import { joinText } from '@/lib/chat/dictation-submit'
import { DictationError, type DictationHandlers, type DictationTransport } from './transport'

/**
 * ElevenLabs Scribe v2 realtime — the pre-loap `use-dictation.ts` socket
 * code, moved verbatim behind `DictationTransport`. The SDK opens its own
 * microphone (VAD commit strategy), so the stream the hook already holds is
 * released first; the permission is granted, so the SDK's prompt is a no-op.
 */
export function createElevenLabsTransport(): DictationTransport {
  let conn: RealtimeConnection | null = null
  let committed = ''

  const close = () => {
    const c = conn
    conn = null
    try {
      c?.close()
    } catch {
      // already closed
    }
  }

  return {
    async start(session, mic, handlers: DictationHandlers) {
      if (session.provider !== 'elevenlabs') throw new DictationError('wrong provider', 'failed')
      for (const track of mic.getTracks()) track.stop()
      committed = ''
      let c: RealtimeConnection
      try {
        c = Scribe.connect({
          token: session.token,
          modelId: session.model,
          languageCode: session.languageCode,
          commitStrategy: CommitStrategy.VAD,
          microphone: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        })
      } catch (err) {
        const denied =
          err instanceof Error && /permission|notallowed|denied/i.test(err.name + err.message)
        throw new DictationError(String(err), denied ? 'mic_denied' : 'failed')
      }
      conn = c
      await new Promise<void>((resolve, reject) => {
        // Before the session started, a failure rejects start() and the hook
        // fails once from its catch; after, only onError fires — never both.
        let settled = false
        c.on(RealtimeEvents.SESSION_STARTED, () => {
          settled = true
          resolve()
        })
        c.on(RealtimeEvents.PARTIAL_TRANSCRIPT, ({ text }) => handlers.onPartial(text))
        c.on(RealtimeEvents.COMMITTED_TRANSCRIPT, ({ text }) => {
          committed = joinText(committed, text.trim())
          handlers.onCommitted(committed)
        })
        const onServiceError = (msg: { error?: string; message?: string } | undefined) => {
          const message = msg?.error ?? msg?.message ?? 'dictation error'
          const err = new DictationError(
            message,
            /permission|denied/i.test(message) ? 'mic_denied' : 'failed'
          )
          if (settled) handlers.onError(err)
          else {
            settled = true
            reject(err)
          }
        }
        for (const event of [
          RealtimeEvents.ERROR,
          RealtimeEvents.AUTH_ERROR,
          RealtimeEvents.QUOTA_EXCEEDED,
          RealtimeEvents.TRANSCRIBER_ERROR,
          RealtimeEvents.RATE_LIMITED,
          RealtimeEvents.INPUT_ERROR,
          RealtimeEvents.INVALID_REQUEST,
          RealtimeEvents.RESOURCE_EXHAUSTED,
          RealtimeEvents.SESSION_TIME_LIMIT_EXCEEDED,
          RealtimeEvents.UNACCEPTED_TERMS,
        ] as const) {
          c.on(event, onServiceError as never)
        }
        c.on(RealtimeEvents.CLOSE, () => {
          if (conn === c) conn = null
          if (!settled) {
            settled = true
            reject(new DictationError('closed', 'failed'))
            return
          }
          handlers.onClose()
        })
      })
    },

    async flush(timeoutMs) {
      const c = conn
      if (!c) return
      await new Promise<void>((resolve) => {
        const done = () => {
          c.off(RealtimeEvents.COMMITTED_TRANSCRIPT, done)
          resolve()
        }
        c.on(RealtimeEvents.COMMITTED_TRANSCRIPT, done)
        try {
          c.commit()
        } catch {
          done()
        }
        setTimeout(done, timeoutMs)
      })
      close()
    },

    close,
  }
}
